import {
	ChipModal,
	ChipModalBody,
	ChipModalField,
	ChipModalFooter,
	ChipModalHeader,
	ChipSelect,
	Textarea,
} from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Link } from "react-router";
import { emptyDocument, fileUrl, readContent, studioCommand, studioKey, studioOptions } from "~/common/studio/api";
import { useAccess } from "~/core/access";
import { ApiError } from "~/core/api";
import { pick, useI18n, useT } from "~/core/i18n";
import presets from "../../../../packages/content/media-presets.json";

export function GenerateImageDialog({
	brandId,
	title,
	open,
	onOpenChange,
	disabled,
	onUse,
}: {
	brandId: string;
	title: string;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	disabled: boolean;
	onUse: (fileId: string, alt: string) => void;
}) {
	const { locale } = useI18n();
	const t = useT();
	const { editor } = useAccess();
	const tr = (en: string, zh: string) => pick(locale, [en, zh]);
	const [prompt, setPrompt] = useState("");
	const [model, setModel] = useState("");
	const [preset, setPreset] = useState("landscape");
	const [itemId, setItemId] = useState("");
	const [submitted, setSubmitted] = useState(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const submitting = useRef(false);
	const options = useQuery({
		queryKey: [...studioKey(brandId), "options"],
		queryFn: () => studioOptions(brandId),
		enabled: open,
	});
	const models = options.data?.models.filter((m) => m.modality === "image") ?? [];
	const selected = models.find((m) => m.id === model);
	const result = useQuery({
		queryKey: [...studioKey(brandId), itemId],
		queryFn: () => readContent(brandId, itemId),
		enabled: open && !!itemId && submitted,
		refetchInterval: (query) => {
			const status = query.state.data?.renders?.[0]?.status;
			return status && !["queued", "running"].includes(status) ? false : 3000;
		},
	});
	const job = result.data?.renders?.[0];
	const asset = result.data?.document.media.find((a) => a.role === "image");
	const file = result.data?.files.find((f) => f.id === asset?.file_id && f.content_type.startsWith("image/"));
	const locked = disabled || !editor || busy;
	async function generate() {
		if (locked || submitted || submitting.current || !selected || !prompt.trim()) return;
		submitting.current = true;
		setBusy(true);
		setError("");
		try {
			const item = await studioCommand<{ id: string; revision: number }>(brandId, "studio.save", {
				title: title.slice(0, 200),
				kind: "gallery",
				asset_only: true,
				locale,
				document: emptyDocument(),
			});
			setItemId(item.id);
			// Once dispatched, inspect the saved work even if the queue response is
			// lost. Repeating a paid request with a new identity could charge twice.
			setSubmitted(true);
			await studioCommand(brandId, "studio.generate", {
				id: item.id,
				expected_revision: item.revision,
				modality: "image",
				model,
				prompt: prompt.trim(),
				preset,
			});
		} catch (e) {
			setError(
				e instanceof ApiError
					? e.localized(locale)
					: tr(
							"Could not confirm generation. Check the saved work before trying again.",
							"无法确认生成状态，请检查已保存的作品后再重试。",
						),
			);
		} finally {
			setBusy(false);
			submitting.current = false;
		}
	}
	return (
		<ChipModal open={open} onOpenChange={onOpenChange} srTitle={tr("Generate image", "生成图片")}>
			<ChipModalHeader onClose={() => onOpenChange(false)} closeAriaLabel={t("common.close")}>
				{tr("Generate image", "生成图片")}
			</ChipModalHeader>
			<ChipModalBody>
				<fieldset className="bare-fieldset stack" disabled={locked || submitted}>
					<ChipModalField type="custom" title={tr("Image description", "图片描述")}>
						<Textarea
							aria-label={tr("Image description", "图片描述")}
							value={prompt}
							onChange={(e) => setPrompt(e.target.value)}
							maxLength={4000}
							rows={4}
							placeholder={tr(`Describe an image for “${title}”`, `描述「${title}」需要的配图`)}
						/>
					</ChipModalField>
					<ChipModalField type="custom" title={tr("Model", "模型")}>
						<ChipSelect
							searchable
							searchPlaceholder={tr("Search models", "搜索模型")}
							aria-label={tr("Model", "模型")}
							value={model}
							onChange={setModel}
							disabled={locked || submitted || options.isPending}
							placeholder={tr("Choose an image model", "选择图片模型")}
							options={models.map((m) => ({ value: m.id, label: m.name || m.id }))}
						/>
					</ChipModalField>
					<ChipModalField type="custom" title={tr("Aspect ratio", "画面比例")}>
						<ChipSelect
							aria-label={tr("Aspect ratio", "画面比例")}
							value={preset}
							onChange={setPreset}
							disabled={locked || submitted}
							options={presets.presets.map((p) => ({
								value: p.id,
								label: `${pick(locale, p.label)} · ${aspectRatio(p.width, p.height)}`,
							}))}
						/>
					</ChipModalField>
				</fieldset>
				<ChipModalField type="custom" title="">
					{selected && (
						<p className="hint">
							{tr(
								`Uses credits at this model's rate: ${selected.credits_per_1000_input.toFixed(2)} / ${selected.credits_per_1000_output.toFixed(2)} per 1k input/output units.`,
								`按模型实际用量扣费：每千输入 / 输出单位 ${selected.credits_per_1000_input.toFixed(2)} / ${selected.credits_per_1000_output.toFixed(2)} 积分。`,
							)}
						</p>
					)}
					{options.isPending && <p role="status">{tr("Loading models…", "正在加载模型…")}</p>}
					{!options.isPending && !options.error && !models.length && (
						<p role="status">{tr("No image models are available right now.", "当前没有可用的图片模型。")}</p>
					)}
					{options.error && (
						<p role="alert">
							{tr("Could not load image models. Reopen to retry.", "图片模型加载失败，请重新打开重试。")}
						</p>
					)}
					{error && <p role="alert">{error}</p>}
					{result.error && (
						<p role="alert">
							{tr("Could not refresh generation status. Reopen to check again.", "生成状态刷新失败，请重新打开查看。")}
						</p>
					)}
					{submitted && !file && !error && (
						<p role="status">{job ? t(`tasks.${job.status}`) : tr("Waiting for generation…", "正在等待生成…")}</p>
					)}
					{file && <img className="w-full" src={fileUrl(file.id)} alt={asset?.alt || prompt} />}
					{itemId && <Link to={`/w/${brandId}/studio/${itemId}`}>{tr("View saved work", "查看已保存的作品")}</Link>}
				</ChipModalField>
			</ChipModalBody>
			<ChipModalFooter
				cancelLabel={t("common.close")}
				onCancel={() => onOpenChange(false)}
				defaultAction="none"
				primaryAction={{
					label: file
						? tr("Use image", "使用图片")
						: submitted
							? tr("Generation submitted", "已提交生成")
							: tr("Generate", "生成"),
					disabled: locked || (!file && (submitted || !selected || !prompt.trim())),
					onClick: () => {
						if (!file) return void generate();
						if (locked) return;
						onUse(file.id, asset?.alt ?? "");
						onOpenChange(false);
						setSubmitted(false);
						setItemId("");
						setError("");
					},
				}}
			/>
		</ChipModal>
	);
}

function aspectRatio(width: number, height: number): string {
	let a = width;
	let b = height;
	while (b) [a, b] = [b, a % b];
	return `${width / a}:${height / a}`;
}
